import React from "react";
import { Button, Select, Input, InputNumber, Upload, ColorPicker, Space, Divider } from "antd";
import {
  DeleteOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  UploadOutlined,
  VerticalAlignTopOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignMiddleOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
} from "@ant-design/icons";
import Label from "./Label";
import { rowHeightFor } from "../utils/quotationUtils";

export default function PropertiesPanel({ el, onChange, onDelete, onReorder, pageW, pageH, margin }) {
  if (!el) {
    return (
      <div style={{ padding: 16, fontSize: 12.5, color: "#8A94A3", lineHeight: 1.6 }}>
        Click an element to select and drag it. Double-click text or a table cell to edit its content. Add new elements from the toolbar above.
        <div style={{ marginTop: 10 }}>
          <b>Tips:</b> orange guide lines appear when an element lines up with the page, the margin, or another element. Use the "Align on page" buttons for exact placement. Press Backspace to delete the selected element, arrow keys to nudge it, and hold Alt while resizing to grow it symmetrically.
        </div>
      </div>
    );
  }

  const field = (labelText, node) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: "#6B7686", marginBottom: 4 }}>{labelText}</div>
      {node}
    </div>
  );

  const isItalic = el.fontStyle === "italic";
  const isUnderline = (el.textDecoration || "").includes("underline");
  const isStrike = (el.textDecoration || "").includes("line-through");

  const toggleDecoration = (kind) => {
    const parts = new Set((el.textDecoration || "none").split(" ").filter((p) => p && p !== "none"));
    if (parts.has(kind)) parts.delete(kind);
    else parts.add(kind);
    onChange(el.id, { textDecoration: parts.size ? [...parts].join(" ") : "none" });
  };

  const toHex = (val) => (typeof val === "string" ? val : val.toHexString());

  return (
    <div style={{ padding: 16 }}>
      <Label>{el.type} properties</Label>

      {(el.type === "text" || el.type === "heading") && (
        <>
          {field("Font size", <InputNumber value={el.fontSize} min={8} max={72} style={{ width: "100%" }} onChange={(v) => onChange(el.id, { fontSize: v || 12 })} />)}
          {field(
            "Weight",
            <Select
              value={el.fontWeight}
              style={{ width: "100%" }}
              onChange={(v) => onChange(el.id, { fontWeight: v })}
              options={[
                { value: 400, label: "Regular" },
                { value: 600, label: "Semibold" },
                { value: 700, label: "Bold" },
              ]}
            />
          )}
          {field(
            "Style",
            <Space.Compact>
              <Button title="Bold" type={el.fontWeight >= 600 ? "primary" : "default"} icon={<BoldOutlined />} onClick={() => onChange(el.id, { fontWeight: el.fontWeight >= 600 ? 400 : 700 })} />
              <Button title="Italic" type={isItalic ? "primary" : "default"} icon={<ItalicOutlined />} onClick={() => onChange(el.id, { fontStyle: isItalic ? "normal" : "italic" })} />
              <Button title="Underline" type={isUnderline ? "primary" : "default"} icon={<UnderlineOutlined />} onClick={() => toggleDecoration("underline")} />
              <Button title="Strikethrough" type={isStrike ? "primary" : "default"} icon={<StrikethroughOutlined />} onClick={() => toggleDecoration("line-through")} />
            </Space.Compact>
          )}
          {field(
            "Align",
            <Select
              value={el.align}
              style={{ width: "100%" }}
              onChange={(v) => onChange(el.id, { align: v })}
              options={[
                { value: "left", label: "Left" },
                { value: "center", label: "Center" },
                { value: "right", label: "Right" },
              ]}
            />
          )}
          {field("Color", <ColorPicker value={el.color} onChange={(c) => onChange(el.id, { color: toHex(c) })} showText style={{ width: "100%" }} />)}
        </>
      )}

      {el.type === "table" && (
        <>
          {field("Font size", <InputNumber value={el.fontSize} min={8} max={24} style={{ width: "100%" }} onChange={(v) => onChange(el.id, { fontSize: v || 12 })} />)}
          {field(
            "Rows",
            <Space>
              <Button
                onClick={() => {
                  const cols = el.rows[0].length;
                  const rowH = rowHeightFor(el);
                  onChange(el.id, { rows: [...el.rows, Array(cols).fill("")], h: el.h + rowH });
                }}
              >
                + Row
              </Button>
              <Button
                onClick={() => {
                  if (el.rows.length <= 1) return;
                  const rowH = rowHeightFor(el);
                  onChange(el.id, { rows: el.rows.slice(0, -1), h: Math.max(20, el.h - rowH) });
                }}
              >
                − Row
              </Button>
            </Space>
          )}
          {field(
            "Columns",
            <Space>
              <Button onClick={() => onChange(el.id, { rows: el.rows.map((r) => [...r, ""]) })}>+ Col</Button>
              <Button
                onClick={() => {
                  if (el.rows[0].length <= 1) return;
                  onChange(el.id, { rows: el.rows.map((r) => r.slice(0, -1)) });
                }}
              >
                − Col
              </Button>
            </Space>
          )}
        </>
      )}

      {(el.type === "line" || el.type === "rect") &&
        field("Color", <ColorPicker value={el.color} onChange={(c) => onChange(el.id, { color: toHex(c) })} showText style={{ width: "100%" }} />)}

      {el.type === "image" && (
        <>
          {field(
            "Upload from device",
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                const reader = new FileReader();
                reader.onload = () => onChange(el.id, { src: reader.result });
                reader.readAsDataURL(file);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} block>
                Choose image
              </Button>
            </Upload>
          )}
          {field(
            "Or image URL",
            <Input placeholder="https://..." value={el.src.startsWith("data:") ? "" : el.src} onChange={(e) => onChange(el.id, { src: e.target.value })} />
          )}
        </>
      )}

      <Divider style={{ margin: "8px 0 14px" }} />

      {field(
        "Position (x, y)",
        <Space.Compact style={{ width: "100%" }}>
          <InputNumber value={Math.round(el.x)} style={{ width: "50%" }} onChange={(v) => onChange(el.id, { x: v || 0 })} />
          <InputNumber value={Math.round(el.y)} style={{ width: "50%" }} onChange={(v) => onChange(el.id, { y: v || 0 })} />
        </Space.Compact>
      )}
      {field(
        "Size (w, h)",
        <Space.Compact style={{ width: "100%" }}>
          <InputNumber value={Math.round(el.w)} style={{ width: "50%" }} onChange={(v) => onChange(el.id, { w: v || 10 })} />
          <InputNumber value={Math.round(el.h)} style={{ width: "50%" }} onChange={(v) => onChange(el.id, { h: v || 10 })} />
        </Space.Compact>
      )}

      {field(
        "Align on page",
        <>
          <Space.Compact style={{ width: "100%", marginBottom: 6 }}>
            <Button title="Align left" style={{ width: "33.33%" }} icon={<AlignLeftOutlined />} onClick={() => onChange(el.id, { x: margin.left })} />
            <Button
              title="Center horizontally"
              style={{ width: "33.33%" }}
              icon={<AlignCenterOutlined />}
              onClick={() => onChange(el.id, { x: Math.round(margin.left + (pageW - margin.left - margin.right - el.w) / 2) })}
            />
            <Button title="Align right" style={{ width: "33.33%" }} icon={<AlignRightOutlined />} onClick={() => onChange(el.id, { x: Math.round(pageW - margin.right - el.w) })} />
          </Space.Compact>
          <Space.Compact style={{ width: "100%" }}>
            <Button title="Align top" style={{ width: "33.33%" }} icon={<VerticalAlignTopOutlined />} onClick={() => onChange(el.id, { y: margin.top })} />
            <Button
              title="Center vertically"
              style={{ width: "33.33%" }}
              icon={<VerticalAlignMiddleOutlined />}
              onClick={() => onChange(el.id, { y: Math.round(margin.top + (pageH - margin.top - margin.bottom - el.h) / 2) })}
            />
            <Button title="Align bottom" style={{ width: "33.33%" }} icon={<VerticalAlignBottomOutlined />} onClick={() => onChange(el.id, { y: Math.round(pageH - margin.bottom - el.h) })} />
          </Space.Compact>
        </>
      )}

      <Space style={{ marginTop: 4, marginBottom: 12 }} wrap>
        <Button icon={<VerticalAlignTopOutlined />} onClick={() => onReorder(el.id, "front")}>
          Bring to front
        </Button>
        <Button icon={<VerticalAlignBottomOutlined />} onClick={() => onReorder(el.id, "back")}>
          Send to back
        </Button>
      </Space>

      <Button danger block icon={<DeleteOutlined />} onClick={() => onDelete(el.id)}>
        Delete element
      </Button>
    </div>
  );
}